/**
 * Enhanced Interview Management Hook
 * Optimized for performance with proper memoization, error handling, and debouncing
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebounce } from './useDebounce';
import useInterviews from './useInterviews';
import supabase from '../utils/supabase';
import { validateInterviewAction } from '../utils/interviewValidation';
import ErrorHandler from '../utils/errorHandler';
import { 
  INTERVIEW_STATUS, 
  PERFORMANCE_CONFIG, 
  DEFAULT_FILTERS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  CHANNELS
} from '../constants/interviewConstants';

/**
 * Enhanced hook for managing interview state and operations
 * @param {string} userId - Current user ID
 * @param {string} userRole - Current user role (admin, employer, va)
 * @param {Object} initialFilters - Initial filter state
 * @returns {Object} Interview management state and operations
 */
export const useInterviewManagement = (userId, userRole, initialFilters = DEFAULT_FILTERS) => {
  // Core state
  const [filters, setFilters] = useState(initialFilters);
  const [activeTab, setActiveTab] = useState('scheduled');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedItems, setSelectedItems] = useState({
    interview: null,
    candidate: null,
    contactCandidate: null,
    pendingStatusChange: null
  });

  // Modal state
  const [modalState, setModalState] = useState({
    schedule: false,
    feedback: false,
    contact: false,
    decision: false,
    timeSlotManager: false,
    interviewDetails: false
  });

  // Calendar state
  const [calendarState, setCalendarState] = useState({
    view: false,
    selectedDate: new Date()
  });

  // Debounced filters for better performance
  const debouncedFilters = useDebounce(filters, PERFORMANCE_CONFIG.DEBOUNCE_DELAY.FILTER);

  // Use the existing interviews hook with debounced filters
  const { 
    interviews, 
    loading, 
    error, 
    stats, 
    operations, 
    refresh 
  } = useInterviews(userId, debouncedFilters);

  // Memoized tabs with counts
  const tabs = useMemo(() => [
    { id: 'scheduled', label: 'Scheduled', count: stats.scheduled },
    { id: 'completed', label: 'Completed', count: stats.completed },
    { id: 'expired', label: 'Expired', count: stats.expired },
    { id: 'cancelled', label: 'Cancelled', count: stats.cancelled },
    { id: 'all', label: 'All Interviews', count: stats.total }
  ], [stats]);

  // Memoized filtered and sorted interviews
  const filteredInterviews = useMemo(() => {
    try {
      // First filter by active tab
      let filtered = interviews;
      const now = new Date();
      
      if (activeTab === 'expired') {
        // Show interviews with expired status
        filtered = interviews.filter(interview => 
          interview.status === 'expired'
        );
      } else if (activeTab !== 'all') {
        filtered = interviews.filter(interview => interview.status === activeTab);
      }
      
      // Apply search query if it exists
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase();
        filtered = filtered.filter(interview => 
          interview.candidate_name?.toLowerCase().includes(query) ||
          interview.job_title?.toLowerCase().includes(query) ||
          interview.notes?.toLowerCase().includes(query)
        );
      }
      
      // Apply sorting
      return [...filtered].sort((a, b) => {
        switch (filters.sortBy) {
          case 'date_asc':
            return new Date(a.scheduled_at) - new Date(b.scheduled_at);
          case 'date_desc':
            return new Date(b.scheduled_at) - new Date(a.scheduled_at);
          case 'name_asc':
            return (a.candidate_name || '').localeCompare(b.candidate_name || '');
          case 'name_desc':
            return (b.candidate_name || '').localeCompare(a.candidate_name || '');
          default:
            return new Date(b.scheduled_at) - new Date(a.scheduled_at);
        }
      });
    } catch (err) {
      ErrorHandler.logError('useInterviewManagement:filteredInterviews', err);
      return [];
    }
  }, [interviews, activeTab, filters.searchQuery, filters.sortBy]);

  // Memoized calendar events for calendar view
  const calendarEvents = useMemo(() => {
    return interviews.map(interview => ({
      id: interview.id,
      title: `${interview.candidate_name || 'Candidate'}: ${interview.job_title || 'Interview'}`,
      start: new Date(interview.scheduled_at),
      end: new Date(new Date(interview.scheduled_at).getTime() + (interview.duration || 60) * 60000),
      status: interview.status,
      interview: interview
    }));
  }, [interviews]);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.dateRange !== DEFAULT_FILTERS.dateRange ||
      filters.interviewType !== DEFAULT_FILTERS.interviewType ||
      filters.hiringStatus !== DEFAULT_FILTERS.hiringStatus ||
      filters.searchQuery.trim() !== ''
    );
  }, [filters]);

  // Real-time subscription with debounced refresh
  useEffect(() => {
    if (!userId) return;
    
    let refreshTimeout;
    const debouncedRefresh = () => {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        refresh();
      }, PERFORMANCE_CONFIG.DEBOUNCE_DELAY.REALTIME_UPDATE);
    };
    
    const interviewChannel = supabase
      .channel(CHANNELS.INTERVIEW_CHANGES)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'interview_schedules',
          filter: `interviewer_id=eq.${userId}`
        },
        debouncedRefresh
      )
      .subscribe();
      
    return () => {
      clearTimeout(refreshTimeout);
      supabase.removeChannel(interviewChannel);
    };
  }, [userId, refresh]);

  // Optimized filter handlers
  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  // Modal management
  const openModal = useCallback((modalType, data = {}) => {
    setModalState(prev => ({ ...prev, [modalType]: true }));
    if (data.interview) setSelectedItems(prev => ({ ...prev, interview: data.interview }));
    if (data.candidate) setSelectedItems(prev => ({ ...prev, candidate: data.candidate }));
    if (data.contactCandidate) setSelectedItems(prev => ({ ...prev, contactCandidate: data.contactCandidate }));
    if (data.pendingStatusChange) setSelectedItems(prev => ({ ...prev, pendingStatusChange: data.pendingStatusChange }));
  }, []);

  const closeModal = useCallback((modalType) => {
    setModalState(prev => ({ ...prev, [modalType]: false }));
    // Clear related selected items when closing modals
    if (modalType === 'schedule') {
      setSelectedItems(prev => ({ ...prev, interview: null, candidate: null }));
    } else if (modalType === 'contact') {
      setSelectedItems(prev => ({ ...prev, contactCandidate: null }));
    } else if (modalType === 'decision') {
      setSelectedItems(prev => ({ ...prev, pendingStatusChange: null }));
    }
  }, []);

  // Interview action handlers with validation and error handling
  const handleScheduleInterview = useCallback((candidateData) => {
    try {
      openModal('schedule', { candidate: candidateData });
    } catch (err) {
      ErrorHandler.logError('useInterviewManagement:handleScheduleInterview', err);
    }
  }, [openModal]);

  const handleReschedule = useCallback(async (interviewId) => {
    try {
      const interview = interviews.find(i => i.id === interviewId);
      if (!interview) {
        throw new Error(ERROR_MESSAGES.INTERVIEW_NOT_FOUND);
      }
      
      const isValid = validateInterviewAction('reschedule', interview, userId, userRole);
      if (!isValid) {
        throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
      }

      openModal('schedule', { interview });
    } catch (err) {
      ErrorHandler.logError('useInterviewManagement:handleReschedule', err);
      throw err; // Re-throw for component to handle UI feedback
    }
  }, [interviews, userId, userRole, openModal]);

  const handleCancel = useCallback(async (interviewId) => {
    try {
      const interview = interviews.find(i => i.id === interviewId);
      if (!interview) {
        throw new Error(ERROR_MESSAGES.INTERVIEW_NOT_FOUND);
      }
      
      const isValid = validateInterviewAction('cancel', interview, userId, userRole);
      if (!isValid) {
        throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
      }

      const result = await operations.cancelInterview(interviewId);
      
      if (result.success) {
        await refresh();
        return { success: true, message: SUCCESS_MESSAGES.INTERVIEW_CANCELLED };
      } else {
        throw new Error(result.error || 'Failed to cancel interview');
      }
    } catch (err) {
      ErrorHandler.logError('useInterviewManagement:handleCancel', err);
      throw err;
    }
  }, [interviews, operations, userId, userRole, refresh]);

  const handleComplete = useCallback(async (interviewId) => {
    try {
      const interview = interviews.find(i => i.id === interviewId);
      if (!interview) {
        throw new Error(ERROR_MESSAGES.INTERVIEW_NOT_FOUND);
      }
      
      const isValid = validateInterviewAction('complete', interview, userId, userRole);
      if (!isValid) {
        throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
      }

      openModal('feedback', { interview });
    } catch (err) {
      ErrorHandler.logError('useInterviewManagement:handleComplete', err);
      throw err;
    }
  }, [interviews, userId, userRole, openModal]);

  const handleMessageCandidate = useCallback(async (interview) => {
    try {
      // Get candidate data
      const { data: candidate, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .eq('id', interview.candidate_id)
        .single();

      if (error) throw error;

      const contactCandidate = {
        id: candidate.id,
        name: candidate.full_name,
        email: candidate.email,
        relatedTo: {
          type: 'interview',
          id: interview.id,
          jobId: interview.job_id,
          title: `Interview for ${interview.job_title}`
        }
      };

      openModal('contact', { contactCandidate });
    } catch (err) {
      ErrorHandler.logError('useInterviewManagement:handleMessageCandidate', err);
      throw err;
    }
  }, [openModal]);

  const handleStatusChange = useCallback(async (interviewId, newStatus) => {
    try {
      const interview = interviews.find(i => i.id === interviewId);
      if (!interview) {
        throw new Error(ERROR_MESSAGES.INTERVIEW_NOT_FOUND);
      }

      // For completion status changes, show decision modal
      if (newStatus === INTERVIEW_STATUS.COMPLETED && interview.status !== INTERVIEW_STATUS.COMPLETED) {
        openModal('decision', { pendingStatusChange: { interview, newStatus } });
        return;
      }

      // For cancellations, use cancelInterview which sends email notifications
      if (newStatus === INTERVIEW_STATUS.CANCELLED) {
        const result = await operations.cancelInterview(interviewId);
        if (result.success) {
          await refresh();
          return { success: true, message: SUCCESS_MESSAGES.INTERVIEW_CANCELLED };
        } else {
          throw new Error(result.error || 'Failed to cancel interview');
        }
      }

      // For other status changes, update directly
      const result = await operations.updateStatus(interviewId, newStatus);

      if (result.success) {
        await refresh();
        return { success: true, message: SUCCESS_MESSAGES.STATUS_UPDATED };
      } else {
        throw new Error(result.error || 'Failed to update interview status');
      }
    } catch (err) {
      ErrorHandler.logError('useInterviewManagement:handleStatusChange', err);
      throw err;
    }
  }, [interviews, operations, openModal, refresh]);

  // Calendar handlers
  const handleCalendarToggle = useCallback(() => {
    setCalendarState(prev => ({ ...prev, view: !prev.view }));
  }, []);

  const handleCalendarDateClick = useCallback((date) => {
    setCalendarState(prev => ({ ...prev, selectedDate: date }));
    handleScheduleInterview({
      name: 'New Candidate',
      email: '',
      job_title: 'Select from applications',
      preselectedDate: date
    });
  }, [handleScheduleInterview]);

  const handleCalendarInterviewClick = useCallback((interview) => {
    openModal('interviewDetails', { interview });
  }, [openModal]);

  return {
    // State
    filters,
    activeTab,
    viewMode,
    modalState,
    calendarState,
    selectedItems,
    
    // Data
    interviews: filteredInterviews,
    allInterviews: interviews,
    loading,
    error,
    stats,
    tabs,
    calendarEvents,
    hasActiveFilters,
    
    // Operations
    operations,
    refresh,
    
    // Handlers
    setActiveTab,
    setViewMode,
    handleFilterChange,
    handleClearFilters,
    openModal,
    closeModal,
    handleScheduleInterview,
    handleReschedule,
    handleCancel,
    handleComplete,
    handleMessageCandidate,
    handleStatusChange,
    handleCalendarToggle,
    handleCalendarDateClick,
    handleCalendarInterviewClick
  };
};